import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { get as commsGet } from "../../../../service/comms/commsApi";
import {
  getCoachPlanAnalytics,
  classifyOversightError,
} from "../../../../service/comms/oversightAnalyticsService";
import { AvatarRing } from "../../../../common/comms/AvatarRing";
import { EmptyState } from "../../../../common/comms/EmptyState";
import { RetryBanner } from "../../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../../common/comms/SkeletonRows";
import { useCommsIdentity } from "../../../../hooks/comms/useCommsIdentity";
import { relativeTime } from "../../../../utils/oversightFormat";
import {
  PLAN_STATE,
  planState,
  planHeadlines,
  needsALook,
  changeDistribution,
  noActivityCopy,
} from "./planDerive";

ChartJS.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const ORANGE = "#FF6414";
const BLUE = "#3B82F6";
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "rgba(226,232,240,0.6)";

/** Graded fill for the change histogram: calm at the left, alarming at the right. */
const CHANGE_COLORS = ["#E5E7EB", "#DBEAFE", "#93C5FD", "#60A5FA", "#EF9F27", "#E24B4A"];

const TONE_ROW = {
  danger: { chip: "bg-danger-bg text-danger-dark", dot: "bg-danger" },
  warn: { chip: "bg-internal-bg text-internal-text", dot: "bg-internal" },
};

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-08-23" → "Aug 23". Parsed manually so a date-only string can't drift a day. */
function shortDate(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr || ""));
  if (!m) return String(dateStr || "");
  return `${MONTH_ABBR[Number(m[2]) - 1]} ${Number(m[3])}`;
}

/** One headline tile. Null values read "—", never 0. */
function Headline({ tile }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-[26px] leading-8 font-bold text-gray-800">
        {tile.value == null ? "—" : tile.value.toLocaleString()}
      </p>
      <p className="mt-1 text-[11px] uppercase tracking-wide text-gray-500">{tile.label}</p>
      {tile.sentence && <p className="mt-1 text-[11px] text-gray-400">{tile.sentence}</p>}
    </div>
  );
}

/**
 * Whose plan work these numbers count. Visible rather than implicit, because
 * the narrow reading produces a "did nothing" verdict about a named person.
 */
function ActorScopeToggle({ value, onChange }) {
  const OPTIONS = [
    { actorOnly: true, label: "Work this coach did" },
    // { actorOnly: false, label: "All plan work on their clients" },
  ];

  return (
    <div
      role="group"
      aria-label="Whose plan work to count"
      className="inline-flex self-start rounded-lg border border-gray-200 p-0.5 bg-white"
    >
      {OPTIONS.map((option) => {
        const isActive = option.actorOnly === value;
        return (
          <button
            key={option.label}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.actorOnly)}
            className={`min-h-[44px] px-3 rounded-md text-xs font-semibold transition-colors ${
              isActive
                ? "bg-light-orange text-orange-primary border border-orange-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ChartCard({ title, hint, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm font-bold text-gray-900">{title}</p>
      {hint && <p className="mt-1 mb-4 text-xs text-gray-500">{hint}</p>}
      <div className="overflow-x-auto">
        <div className="min-w-[22rem] h-56">{children}</div>
      </div>
    </div>
  );
}

/**
 * Plans — "is this coach's plan work any good?"
 *
 * Reads `GET /oversight/analytics/coaches/{id}/plans`, which derives everything
 * from `ChatSchema.PlanEvents`. Two things this component is careful about:
 *
 * `stream_active: false` means the plan-events flag was off for the whole
 * window — nobody's plan work was recorded. Rendering zeroes then would read as
 * "this coach wrote nothing", a different and unfair claim, so that case gets
 * its own panel.
 *
 * Two metrics from the design are absent by necessity, not oversight: "barely
 * changed (<5%)" needs a plan-size denominator the stream does not carry, and
 * "same plan sent again" needs the clone's source id, which is never stored. A
 * pure resend emits no event at all. Both need new emitter fields.
 *
 * @param {Object} props
 * @param {string|null|undefined} props.coachId - Resolved coach id.
 * @param {{date_from: string, date_to: string}} props.range
 * @param {'reports'|'company'} [props.scope] - Report-chain scope, carried from
 *   the coaches list so this page resolves the roster the list resolved.
 */
export function PlansTab({ coachId, range, scope }) {
  const navigate = useNavigate();
  const { getClientName } = useCommsIdentity();

  /**
   * Whose plan work to count. True (the endpoint's own default) counts only
   * work this coach performed; false widens to any staff edit on their roster.
   * Exposed because the narrow reading renders as a flat "did nothing", which
   * is a claim about a person and needs to be checkable.
   */
  const [actorOnly, setActorOnly] = useState(true);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  /** @type {[('forbidden'|'gateway'|'other'|null), Function]} */
  const [errorKind, setErrorKind] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Active-cleanup fetch: refires on coach, range, or retry.
  useEffect(() => {
    if (!coachId) return undefined;
    let active = true;
    setLoading(true);
    setErrorKind(null);
    getCoachPlanAnalytics(commsGet, coachId, { ...range, scope, actor_only: actorOnly })
      .then((data) => {
        if (!active) return;
        setPayload(data);
      })
      .catch((err) => {
        if (!active) return;
        setErrorKind(classifyOversightError(err));
        setPayload(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [coachId, range, scope, actorOnly, reloadToken]);

  const state = planState(payload);
  const tiles = useMemo(() => planHeadlines(payload), [payload]);
  const flagged = useMemo(() => needsALook(payload), [payload]);
  const distribution = useMemo(() => changeDistribution(payload), [payload]);

  const seriesChart = useMemo(() => {
    const rows = Array.isArray(payload?.series) ? payload.series : [];
    if (rows.length === 0) return null;
    return {
      data: {
        labels: rows.map((r) => shortDate(r.date)),
        datasets: [
          {
            label: "Plans handed out",
            data: rows.map((r) => r.plans_handed_out ?? null),
            borderColor: ORANGE,
            backgroundColor: ORANGE,
            borderWidth: 2,
            pointRadius: 2,
            spanGaps: true,
            tension: 0.25,
          },
          {
            label: "Clients who got one",
            data: rows.map((r) => r.clients_reached ?? null),
            borderColor: BLUE,
            backgroundColor: BLUE,
            borderWidth: 2,
            pointRadius: 2,
            spanGaps: true,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "bottom",
            labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, padding: 12, font: { size: 11 } },
          },
        },
        scales: {
          x: { ticks: { maxTicksLimit: 8, font: { size: 10 }, color: AXIS_COLOR }, grid: { display: false } },
          y: {
            beginAtZero: true,
            ticks: { font: { size: 10 }, color: AXIS_COLOR, precision: 0 },
            grid: { color: GRID_COLOR },
          },
        },
      },
    };
  }, [payload]);

  const histogram = useMemo(() => {
    if (distribution.length === 0) return null;
    return {
      data: {
        labels: distribution.map((b) => b.label),
        datasets: [
          {
            label: "Clients",
            data: distribution.map((b) => b.clients),
            backgroundColor: distribution.map((_, i) => CHANGE_COLORS[i] || CHANGE_COLORS.at(-1)),
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (c) => ` ${c.parsed.y} clients` } },
        },
        scales: {
          x: {
            ticks: { font: { size: 11 }, color: AXIS_COLOR },
            grid: { display: false },
            title: {
              display: true,
              text: "editing sessions in this period",
              color: AXIS_COLOR,
              font: { size: 10 },
            },
          },
          y: {
            beginAtZero: true,
            ticks: { font: { size: 10 }, color: AXIS_COLOR, precision: 0 },
            grid: { color: GRID_COLOR },
          },
        },
      },
    };
  }, [distribution]);

  const handleRetry = () => setReloadToken((t) => t + 1);

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonRows variant="metrics" count={4} />
        <SkeletonRows variant="rows" count={4} />
      </div>
    );
  }

  if (errorKind === "forbidden") {
    return (
      <EmptyState
        title="This coach is outside your report chain"
        hint="You don't have access to this coach's plan activity."
      />
    );
  }

  if (errorKind) {
    return (
      <RetryBanner
        message={
          errorKind === "gateway"
            ? "Something went wrong while loading."
            : "Something went wrong loading plan activity."
        }
        onRetry={handleRetry}
      />
    );
  }

  if (state === PLAN_STATE.NOT_RECORDING) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="text-base font-bold text-gray-900">Plan activity wasn't being recorded</h2>
        <p className="mt-1.5 max-w-[70ch] text-sm text-gray-700">
          The plan-events stream logged nothing at all in this period — for any coach, not just
          this one. That means the recording was switched off, so there is no honest number to
          show here. It is deliberately not rendered as zero: that would read as "this coach wrote
          no plans", which is a different claim.
        </p>
        <p className="mt-3 text-[11px] text-gray-400">
          Turn on <code className="font-mono">PLAN_EVENTS_ENABLED</code> in ms-agents, and check the
          diet and workout services have their plan-events endpoint and admin key configured.
        </p>
      </div>
    );
  }

  if (state === PLAN_STATE.NO_ACTIVITY) {
    const copy = noActivityCopy(payload);
    return (
      <EmptyState
        title={copy.title}
        hint={copy.hint}
        action={
          copy.filtered ? (
            <button
              type="button"
              onClick={() => setActorOnly(false)}
              className="min-h-[44px] px-4 text-sm font-medium text-white rounded-lg bg-orange-primary hover:opacity-90"
            >
              Show all plan work on their clients
            </button>
          ) : null
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2.5">
        <h2 className="text-base font-bold text-gray-900">Plans</h2>
        <span className="text-xs text-gray-500">
          What they wrote, who got it, and how often it changed
        </span>
        <span className="sm:ml-auto text-[11px] text-gray-400">
          {payload?.clients_assigned} client{payload?.clients_assigned === 1 ? "" : "s"} assigned
          right now
        </span>
      </div>

      <ActorScopeToggle value={actorOnly} onChange={setActorOnly} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((tile) => (
          <Headline key={tile.id} tile={tile} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-4">
        <ChartCard
          title="Plans handed out vs. clients they reached"
          hint="When the two lines diverge, plans are going to the same few clients."
        >
          {seriesChart ? (
            <Line data={seriesChart.data} options={seriesChart.options} />
          ) : (
            <EmptyState title="No plan activity in this period" />
          )}
        </ChartCard>

        <ChartCard
          title="How often one client's plan changes"
          hint="Bars on the right mean the coach keeps changing their mind. A burst of edits in one sitting counts once."
        >
          {histogram ? <Bar data={histogram.data} options={histogram.options} /> : null}
        </ChartCard>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-baseline gap-2.5 mb-1">
          <h3 className="text-sm font-bold text-gray-900">Worth a look</h3>
          <span className="sm:ml-auto text-[11px] text-gray-400">
            {flagged.length === 0
              ? "nothing flagged"
              : `${flagged.length} client${flagged.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <p className="mb-4 text-xs text-gray-500">
          Each of these is a rule you can change. Thresholds:{" "}
          {payload?.thresholds?.churn_sessions} editing sessions, replaced inside{" "}
          {payload?.thresholds?.replaced_within_hours}h.
        </p>

        {flagged.length === 0 ? (
          <EmptyState title="Nothing looks off" hint="No churn, no plans replaced before the client saw them." />
        ) : (
          <div className="flex flex-col">
            {flagged.map((row) => {
              const name =
                getClientName(row.client_id) || `#${String(row.client_id).slice(-6)}`;
              const colorIndex = String(row.client_id).charCodeAt(0) % 8;
              const tone = TONE_ROW[row.tone] || TONE_ROW.warn;
              return (
                <div
                  key={row.client_id}
                  className="flex flex-wrap sm:flex-nowrap items-center gap-3 py-3 border-t border-gray-100 first:border-t-0"
                >
                  <AvatarRing name={name} colorIndex={colorIndex} size="sm" />
                  <div className="min-w-0 w-40 flex-shrink-0">
                    <p className="text-[13px] font-semibold text-gray-800 truncate">{name}</p>
                    <span
                      className={`inline-flex mt-0.5 px-1.5 py-px rounded-full text-[10px] font-semibold ${tone.chip}`}
                    >
                      {row.flags.includes("replaced_within_a_day")
                        ? "Replaced fast"
                        : "Changed too often"}
                    </span>
                  </div>
                  <p className="flex-1 min-w-0 text-[13px] text-gray-700">{row.reason}</p>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">
                    {row.last_plan_activity_at ? relativeTime(row.last_plan_activity_at) : "—"}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigate(`/coaches/${coachId}?tab=clients`)}
                    className="min-h-[44px] text-[11px] sm:text-xs font-semibold text-orange-primary hover:text-orange-600 whitespace-nowrap"
                  >
                    Find in Clients
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-400 max-w-[80ch]">
        Two checks from the design are missing because the data cannot support them: "barely
        changed" needs the plan's size to measure a percentage against, and "same plan sent again"
        needs the source plan id — a pure resend emits no event at all. Both need new fields on the
        plan-events wire, not a new query.
      </p>
    </div>
  );
}

export default PlansTab;
